import Dropdown from 'components/ui/Dropdown';
import Button from 'components/ui/Button';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Search,
  Users,
  X,
} from 'lucide-react';
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectSegments,
  selectSegmentsSuccess,
  selectSelectedSegment,
  selectSegmentsPagination,
  selectSegmentsLoading,
} from 'store/segments/segments.selector';
import {
  clearSegmentState,
  getSegmentsRequest,
  createSegmentRequest,
} from 'store/segments/segments.action';
import { useDispatch } from 'react-redux';
import { formatDate } from 'utils/helper';
import CreateManualSegmentModal from 'components/segments/CreateManualSegmentModal';
import { useToast } from 'contexts/ToastContext';
import { SegmentFilters } from 'types/segments/segments.types';
import { SegmentTableSkeleton } from 'skeletons';

const Segments = () => {
  const segments = useSelector(selectSegments);
  const pagination = useSelector(selectSegmentsPagination);
  const loading = useSelector(selectSegmentsLoading);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showCreateManualSegmentModal, setShowCreateManualSegmentModal] =
    useState(false);
  const success = useSelector(selectSegmentsSuccess);
  const selectedSegment = useSelector(selectSelectedSegment);
  const { showToast } = useToast();

  // Filter and pagination state
  const [filters, setFilters] = useState<SegmentFilters>({
    page: 0,
    limit: 10,
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    type: undefined,
  });

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized) {
      if (
        pagination &&
        (pagination.page > 0 || pagination.limit !== 10 || pagination.total > 0)
      ) {
        setFilters((prev) => {
          if (
            prev.page !== pagination.page ||
            prev.limit !== pagination.limit
          ) {
            return {
              ...prev,
              page: pagination.page,
              limit: pagination.limit,
            };
          }
          return prev;
        });
      }
      setIsInitialized(true);
    }
  }, [pagination, isInitialized]);

  // Additional filter state for UI
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('All Types');
  const [selectedUsage, setSelectedUsage] = useState('All Usage');
  const [selectedStatus, setSelectedStatus] = useState('Active');

  // Debounce timeout state
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  // Debounced search function
  const debounceSearch = useCallback(
    (searchValue: string) => {
      // Clear previous timeout if exists
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }

      // Set new timeout
      const newTimeout = setTimeout(() => {
        setFilters((prev) => ({
          ...prev,
          search: searchValue,
          page: 0, // Reset to first page when searching
        }));
      }, 1000);

      setDebounceTimeout(newTimeout);
    },
    [debounceTimeout]
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debounceSearch(value);
  };

  // Handle filter changes
  const handleTypeFilter = (type: string) => {
    setSelectedType(type);
    setFilters((prev) => ({
      ...prev,
      page: 0,
      type: type === 'All Types' ? undefined : (type as 'Smart' | 'Manual'),
    }));
  };

  const handleUsageFilter = (usage: string) => {
    setSelectedUsage(usage);
    setFilters((prev) => ({
      ...prev,
      page: 0,
      // Add usage filter logic here if needed on backend
    }));
  };

  const handleStatusFilter = (status: string) => {
    setSelectedStatus(status);
    setFilters((prev) => ({
      ...prev,
      page: 0,
      // Add status filter logic here if needed on backend
    }));
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    if (newPage >= 0 && newPage < pagination?.totalPages) {
      setFilters((prev) => ({
        ...prev,
        page: newPage,
      }));
    }
  };

  const handlePageSizeChange = (newLimit: number) => {
    setFilters((prev) => ({
      ...prev,
      limit: newLimit,
      page: 0, // Reset to first page when changing page size
    }));
  };

  // Fetch segments when filters change
  useEffect(() => {
    if (isInitialized) {
      dispatch(getSegmentsRequest(filters));
    }
  }, [dispatch, filters, isInitialized]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [debounceTimeout]);

  useEffect(() => {
    if (success && selectedSegment) {
      // Refresh the current page after successful creation/update
      dispatch(getSegmentsRequest(filters));
      showToast({
        type: 'success',
        title: 'Segment saved',
        message: 'Segment has been saved successfully',
      });
      // Navigate to the specific segment details page based on type
      if (selectedSegment.type === 'Manual') {
        setShowCreateManualSegmentModal(false);
        navigate(`/segments/manual/${selectedSegment._id}`);
      } else {
        navigate(`/segments/smart/${selectedSegment._id}`);
      }
      dispatch(clearSegmentState());
    }
  }, [success, selectedSegment, navigate, dispatch, showToast, filters]);

  const handleCreateManualSegment = (segment: {
    name: string;
    description: string;
  }) => {
    dispatch(
      createSegmentRequest({
        name: segment.name,
        description: segment.description,
        type: 'Manual',
        conditions: {},
      })
    );
  };

  // Calculate pagination display (0-based indexing)
  const startItem = (pagination?.page || 0) * (pagination?.limit || 10) + 1;
  const endItem = startItem + (pagination?.limit || 10) - 1;
  const hasNextPage = pagination?.page < pagination?.totalPages - 1;
  const hasPrevPage = pagination?.page > 0;
  const hasSegments = segments.length > 0;
  const hasSearch = !!searchTerm;

  return (
    <div className="p-6 space-y-4 bg-background">
      {/* Header - always visible */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-semibold">Segments</h2>
          <p className="text-muted-foreground text-sm mt-1">
            Manage your segments and subscriber lists
          </p>
        </div>
        <Dropdown
          label="+ Create Segment"
          items={[
            {
              label: 'Smart Segment',
              onClick: () => navigate(`/segments/smart`),
            },
            {
              label: 'Manual Segment',
              onClick: () => setShowCreateManualSegmentModal(true),
            },
          ]}
          className="bg-primary text-primary-foreground hover:bg-primary-active text-sm"
        />
      </div>

      {loading ? (
        <SegmentTableSkeleton rows={10} />
      ) : !hasSegments && !hasSearch ? (
        /* Empty state - no segments yet */
        <div className="bg-card rounded-xl border border-border">
          <div className="flex flex-col items-center justify-center py-20 px-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-card-foreground mb-2">
                No segments yet
              </h3>
              <p className="text-muted-foreground mb-8 max-w-md">
                Get started by creating your first segment. Create a smart
                segment with conditions or a manual segment and add contacts
                directly.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="primary"
                  onClick={() => navigate('/segments/smart')}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create Smart Segment
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCreateManualSegmentModal(true)}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Create Manual Segment
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : !hasSegments && hasSearch ? (
        /* Empty state - search returned no results */
        <>
          {/* Search and Filter Controls */}
          <div className="mb-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 max-w-md">
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search segments by name or ID..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="block w-full pl-12 pr-4 py-2.5 border border-border rounded-lg bg-card text-card-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilters((prev) => ({
                          ...prev,
                          search: '',
                          page: 0,
                        }));
                      }}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-xl border border-border">
            <div className="flex flex-col items-center justify-center py-20 px-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Search className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-card-foreground mb-2">
                  No segments found
                </h3>
                <p className="text-muted-foreground mb-6">
                  No segments match your search for{' '}
                  <span className="font-medium text-card-foreground">
                    "{searchTerm}"
                  </span>
                </p>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setFilters((prev) => ({ ...prev, search: '', page: 0 }));
                  }}
                  className="inline-flex items-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  Clear Search
                </Button>
              </div>
            </div>
          </div>
        </>
      ) : (
        /* Has segments - show search, filters, table */
        <div>
          {/* Search and Filter Controls */}
          <div className="mb-6">
            <div className="flex items-center justify-between gap-4">
              {/* Search Section */}
              <div className="flex-1 max-w-md">
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-muted-foreground group-focus-within:text-primary transition-colors" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search segments by name or ID..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="block w-full pl-12 pr-4 py-2.5 border border-border rounded-lg bg-card text-card-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setFilters((prev) => ({
                          ...prev,
                          search: '',
                          page: 0,
                        }));
                      }}
                      className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Results Per Page Section */}
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Show:
                  </span>
                  <div className="relative">
                    <select
                      value={pagination?.limit || 10}
                      onChange={(e) =>
                        handlePageSizeChange(Number(e.target.value))
                      }
                      className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary shadow-sm hover:shadow-md cursor-pointer"
                    >
                      <option value={5}>5 per page</option>
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="hidden lg:block h-8 w-px bg-gray-300"></div>

                {/* Filter Section */}
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Filters:
                  </span>
                  <div className="relative">
                    <select
                      value={selectedType}
                      onChange={(e) => handleTypeFilter(e.target.value)}
                      className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-3 pr-8 text-sm font-medium text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary shadow-sm hover:shadow-md cursor-pointer min-w-[120px]"
                    >
                      <option>All Types</option>
                      <option>Smart</option>
                      <option>Manual</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div className="relative">
                    <select
                      value={selectedUsage}
                      onChange={(e) => handleUsageFilter(e.target.value)}
                      disabled
                      className="appearance-none bg-gray-100 border border-gray-300 rounded-lg px-4 py-3 pr-8 text-sm font-medium text-gray-500 cursor-not-allowed min-w-[120px]"
                    >
                      <option>All Usage</option>
                      <option>Used</option>
                      <option>Unused</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                  <div className="relative">
                    <select
                      value={selectedStatus}
                      onChange={(e) => handleStatusFilter(e.target.value)}
                      disabled
                      className="appearance-none bg-gray-100 border border-gray-300 rounded-lg px-4 py-3 pr-8 text-sm font-medium text-gray-500 cursor-not-allowed min-w-[100px]"
                    >
                      <option>Active</option>
                      <option>Inactive</option>
                      <option>All</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[200px]">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[100px]">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                    Contacts
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-foreground min-w-[120px]">
                    Usage
                  </th>
                </tr>
              </thead>
              <tbody>
                {segments.map((segment) => (
                  <tr
                    key={segment._id}
                    className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap min-w-[200px]">
                      <div className="space-y-1">
                        <Link
                          to={
                            segment.type === 'Manual'
                              ? `/segments/manual/${segment._id}`
                              : `/segments/smart/${segment._id}`
                          }
                          className="text-sm inline-flex items-center font-medium hover:text-primary-hover cursor-pointer hover:underline"
                        >
                          {segment.name}
                          <ChevronRight className="w-4 h-4 ml-1 inline" />
                        </Link>
                        <div className="text-xs text-muted-foreground">
                          Created at {formatDate(segment.createdAt)} | Last
                          edited {formatDate(segment.updatedAt)}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap min-w-[100px]">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted/50 text-foreground">
                        {segment.type === 'Smart' ? 'Smart' : 'Manual'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                      <div className="text-sm text-primary font-semibold">
                        {segment.contacts?.length || 0} people
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap min-w-[120px]">
                      <div className="text-sm text-muted-foreground">
                        Unused
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {!loading && segments.length > 0 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-muted-foreground">
            {startItem}-{endItem} of {pagination?.total || 0} segments
          </div>

          <div className="flex items-center space-x-2">
            <button
              className={`p-2 rounded border rounded-lg border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${!hasPrevPage ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!hasPrevPage}
              onClick={() => handlePageChange((pagination?.page || 0) - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <span className="text-sm text-muted-foreground px-3 py-1">
              Page {(pagination?.page || 0) + 1} of{' '}
              {pagination?.totalPages || 0}
            </span>

            <button
              className={`p-2 rounded border rounded-lg border-border hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed ${!hasNextPage ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!hasNextPage}
              onClick={() => handlePageChange((pagination?.page || 0) + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {showCreateManualSegmentModal && (
        <CreateManualSegmentModal
          isOpen={showCreateManualSegmentModal}
          onClose={() => setShowCreateManualSegmentModal(false)}
          onSubmit={handleCreateManualSegment}
        />
      )}
    </div>
  );
};

export default Segments;
